// src/components/generic-crud/GenericCardView.js
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { alpha, useTheme, keyframes } from '@mui/material/styles';
import {
  Grid,
  Card,
  Typography,
  IconButton,
  Stack,
  Box,
  Switch,
  Avatar,
  Tooltip,
} from '@mui/material';
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';

// ─── Animated shimmer for the accent strip ───
const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

// Accent color – follows the active Settings Preset (theme.palette.primary.main)
// When user switches preset to Orange, all cards turn orange; Indigo → indigo, etc.

// Status dot color map
function getStatusDotColor(status, theme) {
  const map = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  };
  return map[status] || theme.palette.grey[500];
}

export default function GenericCardView({
  config,
  data,
  onEdit,
  onDuplicate,
  onDelete,
  features,
  tableConfig,
  table,
  currentUserId,
  visibleFields,
  fullConfig,
  limitReached,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const effectiveConfig = useMemo(() => {
    const firstCol = tableConfig?.columns?.[0];
    const secondCol = tableConfig?.columns?.[1];

    return {
      title: config?.title || ((row) => row.name || row.title || row.label || (firstCol ? row[firstCol.id] : '') || row._id || ''),
      subtitle: config?.subtitle || ((row) => (secondCol && secondCol.id !== 'status' ? row[secondCol.id] : '') || row.description || ''),
      avatar: config?.avatar || null,
      status: config?.status || ((row) => {
        if (!row.status) return null;
        const s = String(row.status).toLowerCase();
        let color = 'default';
        if (s === 'active' || s === 'approved' || s === 'published' || s === 'completed') color = 'success';
        if (s === 'pending' || s === 'in_progress') color = 'warning';
        if (s === 'inactive' || s === 'rejected' || s === 'cancelled') color = 'error';
        return {
          label: row.status.charAt(0).toUpperCase() + row.status.slice(1),
          color,
        };
      }),
      metadata: config?.metadata || ((tableConfig?.columns || [])
        .filter((col) => col.id !== 'name' && col.id !== 'title' && col.id !== 'status')
        .map((col) => ({
          id: col.id,
          label: col.label,
          value: (r) => (col.render ? col.render(r[col.id], r) : r[col.id]),
        }))),
    };
  }, [config, tableConfig]);

  return (
    <Box>
      {data && data.length > 0 ? (
        <Grid container spacing={3}>
          {data.map((item, idx) => {
            const row = item;
            const statusInfo = effectiveConfig.status ? effectiveConfig.status(row) : null;

            // 🛡️ Match Ownership & Action Visibility Logic from GenericTableRow
            const getIsOwner = (rowData) => {
              const owner = rowData.createdBy || rowData.userId || rowData.expense_by;
              const ownerId = typeof owner === 'object' ? owner?._id || owner?.id : owner;
              return ownerId === currentUserId;
            };

            const isOwner = getIsOwner(row);

            const editEnabled =
              typeof features.edit === 'function' ? features.edit(row) : features.edit !== false;
            const deleteEnabled =
              typeof features.delete === 'function' ? features.delete(row) : features.delete !== false;
            const duplicateEnabled =
              typeof features.duplicate === 'function'
                ? features.duplicate(row)
                : features.duplicate === true;

            const canEdit =
              editEnabled && (features.isAdmin || features.editAll || (features.editSelf && isOwner));
            const canDelete =
              deleteEnabled &&
              (features.isAdmin || features.deleteAll || (features.deleteSelf && isOwner));
            const canDuplicate = duplicateEnabled && !limitReached;

            const visibleCustomActions = (features.customActions || []).filter((action) => {
              if (action.condition && !action.condition(row)) return false;
              return true;
            });

            // Handle approval switch logic
            const showApproveSwitch = tableConfig?.onToggleApprove && row.status;
            const isToggling = tableConfig?.togglingRows?.has(row._id || row.id);
            const optimisticStatus = tableConfig?.statusOverrides?.[row._id || row.id];
            const currentStatus = optimisticStatus || row.status;

            // Determine what the switch is actually bound to
            let isSwitchChecked = currentStatus === 'approved';
            if (tableConfig?.columns) {
              const switchCol = tableConfig.columns.find(col => col.type === 'switch' || (col.render && typeof col.render(row[col.id], row) === 'object' && col.render(row[col.id], row)?.type === 'switch'));
              if (switchCol) {
                isSwitchChecked = !!row[switchCol.id];
              } else if (row.show_in_app !== undefined) {
                isSwitchChecked = !!row.show_in_app;
              }
            } else if (row.show_in_app !== undefined) {
              isSwitchChecked = !!row.show_in_app;
            }

            const srNo = (table?.page || 0) * (table?.rowsPerPage || 10) + idx + 1;

            const title = typeof effectiveConfig.title === 'function' ? effectiveConfig.title(row) : '';
            const avatarSrc = effectiveConfig.avatar ? effectiveConfig.avatar(row) : null;
            const subtitle = effectiveConfig.subtitle ? effectiveConfig.subtitle(row) : null;
            const accent = theme.palette.primary.main;

            const visibleMetadata = Array.isArray(effectiveConfig.metadata)
              ? effectiveConfig.metadata.filter((meta) => !meta.id || !visibleFields || visibleFields[meta.id])
              : [];

            return (
              <Grid item xs={12} sm={6} md={4} key={row.id || row._id}>
                <Card
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)',
                    boxShadow: isDark
                      ? `0 2px 16px ${alpha('#000', 0.3)}`
                      : `0 2px 16px ${alpha(theme.palette.grey[500], 0.12)}`,
                    border: `1px solid ${alpha(theme.palette.grey[500], isDark ? 0.12 : 0.08)}`,
                    '&:hover': {
                      transform: 'translateY(-4px) scale(1.01)',
                      boxShadow: `0 8px 32px ${alpha(accent, 0.2)}, 0 4px 16px ${alpha(accent, 0.12)}`,
                      borderColor: alpha(accent, 0.3),
                      '& .accent-strip': {
                        width: 6,
                      },
                      '& .avatar-ring': {
                        boxShadow: `0 0 0 3px ${alpha(accent, 0.25)}`,
                      },
                      '& .action-bar': {
                        bgcolor: alpha(accent, isDark ? 0.06 : 0.03),
                      },
                    },
                  }}
                >
                  {/* ─── Accent Strip (Left Edge) ─── */}
                  <Box
                    className="accent-strip"
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      background: `linear-gradient(180deg, ${accent}, ${alpha(accent, 0.4)})`,
                      backgroundSize: '200% 200%',
                      animation: `${shimmer} 4s ease infinite`,
                      transition: 'width 0.3s ease',
                      zIndex: 2,
                    }}
                  />

                  {/* ─── Top Section: Avatar + Name + Status ─── */}
                  <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, pl: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      {/* Avatar */}
                      <Avatar
                        className="avatar-ring"
                        alt={title}
                        // src={avatarSrc}
                        src={typeof avatarSrc === 'string' ? avatarSrc : undefined}
                        sx={{
                          width: 56,
                          height: 56,
                          fontSize: '1.25rem',
                          fontWeight: 700,
                          bgcolor: alpha(accent, 0.15),
                          color: accent,
                          border: `2px solid ${alpha(accent, 0.2)}`,
                          boxShadow: `0 0 0 0px transparent`,
                          transition: 'all 0.3s ease',
                          flexShrink: 0,
                        }}
                      >
                        {/* {!avatarSrc && title ? title.charAt(0).toUpperCase() : null} */}
                        {(() => {
                          if (typeof avatarSrc === 'object' && avatarSrc?.type === 'icon') {
                            return <Iconify icon={avatarSrc.icon} width={28} />;
                          }
                          if (typeof avatarSrc === 'object' && avatarSrc?.type === 'text') {
                            return avatarSrc.text;
                          }
                          if (!avatarSrc && title) {
                            return title.charAt(0).toUpperCase();
                          }
                          return null;
                        })()}
                      </Avatar>

                      {/* Name & Subtitle */}
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: 'text.secondary',
                              bgcolor: alpha(theme.palette.grey[500], 0.16),
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1,
                            }}
                          >
                            #{srNo}
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            noWrap
                            sx={{
                              fontWeight: 700,
                              letterSpacing: -0.2,
                              lineHeight: 1.35,
                            }}
                          >
                            {title}
                          </Typography>

                          {/* Status Dot */}
                          {statusInfo && (
                            <Tooltip title={statusInfo.label} arrow placement="top">
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: getStatusDotColor(statusInfo.color, theme),
                                  boxShadow: `0 0 6px ${alpha(
                                    getStatusDotColor(statusInfo.color, theme),
                                    0.6
                                  )}`,
                                  animation: `${pulseGlow} 2s ease-in-out infinite`,
                                  flexShrink: 0,
                                }}
                              />
                            </Tooltip>
                          )}
                        </Stack>

                        {subtitle && (
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{
                              display: 'block',
                              color: 'text.disabled',
                              fontWeight: 500,
                              mt: 0.25,
                              letterSpacing: 0.3,
                            }}
                          >
                            {subtitle}
                          </Typography>
                        )}

                        {/* Label badge below subtitle */}
                        {statusInfo && (
                          <Label
                            variant="soft"
                            color={statusInfo.color}
                            sx={{
                              mt: 0.75,
                              fontSize: '0.65rem',
                              height: 22,
                              fontWeight: 600,
                              letterSpacing: 0.4,
                              textTransform: 'uppercase',
                            }}
                          >
                            {statusInfo.label}
                          </Label>
                        )}
                      </Box>

                      {/* Approval Switch */}
                      {showApproveSwitch && (
                        <Tooltip title={isSwitchChecked ? 'Enabled' : 'Disabled'} arrow>
                          <Switch
                            checked={isSwitchChecked}
                            disabled={isToggling}
                            onChange={(e) => tableConfig.onToggleApprove(row, e.target.checked)}
                            size="small"
                            color="success"
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>

                  {/* ─── Metadata Grid (2-column tiles) ─── */}
                  <Box sx={{ px: 2.5, pl: 3, pt: 0.5, pb: 1.5, flex: 1 }}>
                    <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1}>
                      {visibleMetadata.map((meta, index) => {
                        const metaValue = meta.value(row, fullConfig);
                        return (
                          <Box
                            key={index}
                            sx={{
                              px: 1.5,
                              py: 1,
                              borderRadius: 1.5,
                              bgcolor: alpha(theme.palette.grey[500], isDark ? 0.08 : 0.04),
                              border: `1px solid ${alpha(
                                theme.palette.grey[500],
                                isDark ? 0.1 : 0.06
                              )}`,
                              transition: 'all 0.2s ease',
                              cursor: 'default',
                              '&:hover': {
                                bgcolor: alpha(accent, isDark ? 0.08 : 0.04),
                                borderColor: alpha(accent, 0.15),
                              },
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              {meta.icon && (
                                <Iconify
                                  icon={meta.icon}
                                  width={16}
                                  sx={{ color: accent, opacity: 0.8, flexShrink: 0 }}
                                />
                              )}
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: 'text.disabled',
                                    fontWeight: 600,
                                    fontSize: '0.6rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.6,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {meta.label}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    color: 'text.primary',
                                    lineHeight: 1.4,
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {metaValue || '—'}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* ─── Action Bar (Frosted Bottom) ─── */}
                  <Box
                    className="action-bar"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.75,
                      px: 2,
                      py: 1.25,
                      bgcolor: alpha(theme.palette.grey[500], isDark ? 0.06 : 0.03),
                      borderTop: `1px solid ${alpha(theme.palette.grey[500], isDark ? 0.1 : 0.06)}`,
                      transition: 'background-color 0.3s ease',
                    }}
                  >
                    {/* Custom Actions */}
                    {visibleCustomActions.map((action, index) => {
                      let iconName = action.icon;
                      if (iconName === 'check') iconName = 'solar:check-circle-bold';
                      else if (iconName === 'close') iconName = 'solar:close-circle-bold';

                      return (
                        <Tooltip key={`custom-action-${index}`} title={action.label} arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const actionHandler = fullConfig?.onCustomAction || tableConfig?.onCustomAction;
                              if (actionHandler) {
                                actionHandler(action.action, row, {
                                  onEdit: () => onEdit(row.id),
                                  onDelete: () => onDelete(row.id),
                                });
                              }
                            }}
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: 1.5,
                              color: action.color ? `${action.color}.main` : 'text.secondary',
                              bgcolor: alpha(theme.palette.grey[500], isDark ? 0.1 : 0.06),
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: alpha(
                                  action.color ? theme.palette[action.color]?.main || accent : accent,
                                  0.15
                                ),
                                color: action.color ? `${action.color}.main` : accent,
                                transform: 'translateY(-1px)',
                              },
                            }}
                          >
                            <Iconify icon={iconName} width={17} />
                          </IconButton>
                        </Tooltip>
                      );
                    })}

                    {/* Duplicate */}
                    {canDuplicate && (
                      <Tooltip title="Duplicate" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onDuplicate(row.id)}
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            color: 'text.secondary',
                            bgcolor: alpha(theme.palette.grey[500], isDark ? 0.1 : 0.06),
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.info.main, 0.12),
                              color: 'info.main',
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          <Iconify icon="solar:copy-bold" width={17} />
                        </IconButton>
                      </Tooltip>
                    )}

                    {/* Edit */}
                    {canEdit && (
                      <Tooltip title="Edit" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(row.id)}
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            color: 'text.secondary',
                            bgcolor: alpha(theme.palette.grey[500], isDark ? 0.1 : 0.06),
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: 'primary.main',
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          <Iconify icon="solar:pen-bold" width={17} />
                        </IconButton>
                      </Tooltip>
                    )}

                    {/* Delete */}
                    {canDelete && (
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onDelete(row.id)}
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            color: 'text.secondary',
                            bgcolor: alpha(theme.palette.grey[500], isDark ? 0.1 : 0.06),
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.error.main, 0.12),
                              color: 'error.main',
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          <Iconify icon="solar:trash-bin-trash-bold" width={17} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
          <Iconify icon="solar:box-minimalistic-bold-duotone" width={80} sx={{ color: 'text.disabled', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary">No Data Found</Typography>
        </Stack>
      )}
    </Box>
  );
}

GenericCardView.propTypes = {
  config: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  features: PropTypes.object,
  tableConfig: PropTypes.shape({
    onToggleApprove: PropTypes.func,
    togglingRows: PropTypes.object,
    statusOverrides: PropTypes.object,
    onCustomAction: PropTypes.func,
    columns: PropTypes.array,
  }),
  table: PropTypes.object,
  currentUserId: PropTypes.string,
  visibleFields: PropTypes.object,
  fullConfig: PropTypes.object,
  limitReached: PropTypes.bool,
};
